// Bank linking methods — Plaid (primary) + Institutional Transfer + Manual.
// Plaid is the recommended flow (verified & instant); if Plaid is not configured
// on this server (missing env vars), the button is disabled with "not configured"
// (matches SSO's behaviour — no mocks).
import React, { useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { toast } from 'sonner';
import {
  Landmark, ShieldCheck, ArrowLeftRight, Loader2, ExternalLink,
} from 'lucide-react';
import api from '@/lib/api';

// ─────────────────────────── PLAID ───────────────────────────

const PlaidButton = ({ onLinked }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const start = async () => {
    setErr('');
    setBusy(true);
    try {
      const r = await api.post('/participant/bank-accounts/plaid/link-token');
      setLinkToken(r.data.link_token);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not start Plaid link.';
      setErr(detail);
      setBusy(false);
    }
  };

  const onSuccess = async (public_token, metadata) => {
    try {
      const r = await api.post('/participant/bank-accounts/plaid/exchange', {
        public_token,
        account_id: metadata?.accounts?.[0]?.id,
        institution_name: metadata?.institution?.name,
      });
      if (!r.data.identity_match_passed) {
        toast.warning(
          `Linked — identity name differs (likely a joint account). ${r.data.verification_notes || ''}`
        );
      } else {
        toast.success(`Linked ${r.data.bank_name} ${r.data.masked_account}`);
      }
      onLinked?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Plaid exchange failed.');
    } finally {
      setBusy(false);
      setLinkToken(null);
    }
  };

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: (error) => {
      setBusy(false);
      setLinkToken(null);
      if (error) setErr(error.display_message || error.error_message || 'Link cancelled.');
    },
  });

  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  return (
    <div data-testid="plaid-link-button-wrapper">
      <button
        data-testid="plaid-link-button"
        onClick={start}
        disabled={busy}
        className="inline-flex items-center gap-2 h-10 px-4 rounded-full text-xs font-semibold bg-[#1B2A4A] text-white hover:bg-[#0F1A33] disabled:opacity-50"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
        Link via Plaid (Recommended)
      </button>
      {err && (
        <p className="text-[11px] text-red-600 mt-1" data-testid="plaid-link-error">{err}</p>
      )}
    </div>
  );
};

const DisabledPlaidButton = () => (
  <button
    data-testid="plaid-link-button-disabled"
    disabled
    title="Plaid is not configured on this server. Ask your administrator to set PLAID_CLIENT_ID, PLAID_SECRET and PLAID_ENV."
    className="inline-flex items-center gap-2 h-10 px-4 rounded-full text-xs font-semibold bg-[#9CA3AF] text-white cursor-not-allowed"
  >
    <ShieldCheck size={13} />
    Link via Plaid (not configured)
  </button>
);

export const PlaidSection = ({ onLinked }) => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api
      .get('/participant/bank-accounts/plaid/status')
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ enabled: false }));
  }, []);

  return (
    <div className="panel p-4 border-l-4 border-l-[#1B2A4A]" data-testid="plaid-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#1B2A4A]" />
            <h3 className="text-sm font-semibold text-[#1B2A4A]">
              Link a Bank Account via Plaid
            </h3>
          </div>
          <p className="text-[11px] text-[#6B7280] mt-1 max-w-2xl">
            Plaid verifies your bank and account owner in seconds without
            micro-deposits. Accounts linked via Plaid are <b>active immediately</b>
            {' '}and can be used for disbursements and loan proceeds.
          </p>
          <p className="text-[10px] text-[#9CA3AF] mt-1">
            Plaid is a FedRAMP-moderate-authorized financial connectivity provider.
          </p>
        </div>
        <div>
          {status?.enabled ? (
            <PlaidButton onLinked={onLinked} />
          ) : (
            <DisabledPlaidButton />
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────── INSTITUTIONAL TRANSFER ───────────────────────────

const TRANSFER_KINDS = [
  { value: 'ira_rollover', label: 'Traditional IRA rollover' },
  { value: 'roth_conversion', label: 'Roth conversion / rollover' },
  { value: '401k_rollover', label: '401(k) / 403(b) / 457(b) rollover' },
  { value: 'tsp_transfer_in', label: 'Transfer in from another UniFed Core account' },
  { value: 'other', label: 'Other qualified transfer' },
];

export const InstitutionalTransferSection = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    firm_name: '',
    firm_contact: '',
    firm_phone: '',
    account_number_at_firm: '',
    account_type_at_firm: '',
    estimated_amount: '',
    transfer_kind: 'ira_rollover',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const load = () => {
    setLoading(true);
    api
      .get('/participant/institutional-transfer')
      .then((r) => setRequests(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const input =
    'w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20';

  const submit = async () => {
    setErr('');
    const amt = parseFloat(form.estimated_amount);
    if (!form.firm_name || form.firm_name.trim().length < 2) {
      setErr('Firm name is required.');
      return;
    }
    if (!form.account_type_at_firm) {
      setErr('Account type is required.');
      return;
    }
    if (isNaN(amt) || amt < 0) {
      setErr('Estimated amount must be non-negative.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/participant/institutional-transfer', {
        ...form,
        estimated_amount: amt,
      });
      toast.success('Institutional transfer request submitted for review');
      setShowForm(false);
      setForm({
        firm_name: '',
        firm_contact: '',
        firm_phone: '',
        account_number_at_firm: '',
        account_type_at_firm: '',
        estimated_amount: '',
        transfer_kind: 'ira_rollover',
        notes: '',
      });
      load();
    } catch (e) {
      const d = e?.response?.data?.detail || 'Submission failed';
      setErr(Array.isArray(d) ? d[0]?.msg || 'Submission failed' : d);
    } finally {
      setSubmitting(false);
    }
  };

  const fmtMoney = (n) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  return (
    <div className="panel p-4" data-testid="institutional-transfer-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2">
            <ArrowLeftRight size={14} className="text-[#1B2A4A]" />
            <h3 className="text-sm font-semibold text-[#1B2A4A]">Institutional Transfer</h3>
          </div>
          <p className="text-[11px] text-[#6B7280] mt-1 max-w-2xl">
            Prefer a direct firm-to-firm rollover (ACAT, 401(k) rollover, IRA transfer)
            instead of a bank-to-bank ACH? Submit the firm and account details below;
            an operations specialist will contact your firm to initiate the transfer.
          </p>
        </div>
        <button
          data-testid="itr-new-btn"
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-medium text-[#1B2A4A] border border-[#D1D5DB] hover:border-[#1B2A4A] hover:bg-gray-50"
        >
          <ExternalLink size={12} />
          {showForm ? 'Close' : 'New Transfer Request'}
        </button>
      </div>

      {showForm && (
        <div className="mt-4 border-t border-[#E2E5E9] pt-4 space-y-3" data-testid="itr-form">
          {err && (
            <div
              className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700"
              data-testid="itr-form-error"
            >
              {err}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Originating Firm (e.g. Fidelity, Vanguard)">
              <input
                data-testid="itr-firm-name"
                value={form.firm_name}
                onChange={(e) => setForm({ ...form, firm_name: e.target.value })}
                className={input}
                maxLength={200}
              />
            </Field>
            <Field label="Transfer Type">
              <select
                data-testid="itr-kind"
                value={form.transfer_kind}
                onChange={(e) => setForm({ ...form, transfer_kind: e.target.value })}
                className={input}
              >
                {TRANSFER_KINDS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Account Type at Firm">
              <input
                data-testid="itr-account-type"
                value={form.account_type_at_firm}
                onChange={(e) => setForm({ ...form, account_type_at_firm: e.target.value })}
                placeholder="Traditional IRA"
                className={input}
                maxLength={80}
              />
            </Field>
            <Field label="Account Number at Firm (optional)">
              <input
                data-testid="itr-account-number"
                value={form.account_number_at_firm}
                onChange={(e) => setForm({ ...form, account_number_at_firm: e.target.value })}
                className={input + ' font-mono'}
                maxLength={60}
              />
            </Field>
            <Field label="Firm Contact Person (optional)">
              <input
                data-testid="itr-firm-contact"
                value={form.firm_contact}
                onChange={(e) => setForm({ ...form, firm_contact: e.target.value })}
                className={input}
                maxLength={200}
              />
            </Field>
            <Field label="Firm Phone (optional)">
              <input
                data-testid="itr-firm-phone"
                value={form.firm_phone}
                onChange={(e) => setForm({ ...form, firm_phone: e.target.value })}
                className={input}
                maxLength={40}
              />
            </Field>
            <Field label="Estimated Transfer Amount (USD)">
              <input
                type="number"
                min="0"
                step="0.01"
                data-testid="itr-amount"
                value={form.estimated_amount}
                onChange={(e) => setForm({ ...form, estimated_amount: e.target.value })}
                className={input + ' font-mono'}
              />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea
              data-testid="itr-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              maxLength={2000}
              className={input + ' h-auto py-2'}
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowForm(false)}
              className="h-10 px-4 rounded text-xs font-medium text-[#6B7280] border border-[#D1D5DB] hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              data-testid="itr-submit-btn"
              onClick={submit}
              disabled={submitting}
              className="h-10 px-4 rounded text-xs font-semibold text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-50 inline-flex items-center gap-2"
            >
              {submitting && <Loader2 size={12} className="animate-spin" />}
              Submit Transfer Request
            </button>
          </div>
        </div>
      )}

      {/* List existing requests */}
      {!loading && requests.length > 0 && (
        <div className="mt-4 border-t border-[#E2E5E9] pt-3" data-testid="itr-list">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280] mb-2">
            Your transfer requests
          </p>
          <div className="divide-y divide-[#F0F1F3]">
            {requests.map((r) => (
              <div
                key={r.id}
                data-testid={`itr-row-${r.id}`}
                className="flex items-center justify-between py-2 text-xs gap-3 flex-wrap"
              >
                <div className="flex-1 min-w-[200px]">
                  <span className="font-semibold text-[#1B2A4A]">{r.firm_name}</span>
                  <span className="text-[#6B7280] ml-2">· {r.account_type_at_firm}</span>
                  {r.account_number_at_firm_last4 && (
                    <span className="text-[#9CA3AF] ml-2 font-mono">
                      ••••{r.account_number_at_firm_last4}
                    </span>
                  )}
                </div>
                <div className="font-mono text-[#1B2A4A]">{fmtMoney(r.estimated_amount)}</div>
                <div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-amber-50 border-amber-200 text-amber-700 uppercase tracking-wider">
                    {(r.status || 'pending').replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">
      {label}
    </label>
    {children}
  </div>
);

// Attached for convenience: a tiny placeholder used when the consuming panel
// is deciding whether to show Plaid or the legacy manual form.
export const LegacyManualLinkBadge = () => (
  <span className="inline-flex items-center gap-1 text-[10px] text-[#9CA3AF]" data-testid="manual-link-label">
    <Landmark size={10} /> Manual routing/account entry (micro-deposit verification)
  </span>
);
